import type { ProductConfigPresetSchema } from '~offerFlow/schemas/ProductConfigPreset';
import * as v from 'valibot';
import { ProductConfigPresetSchemaActivity, ProductConfigPresetSchemaP2P } from '~offerFlow/schemas/ProductConfigPreset';

/**
 * Parse and validate product config presets from a URL query parameter (?cp=...).
 * Resolves place URNs to names via API.
 * Returns the validated presets object, or undefined if invalid.
 * --------------------------------------------------------------------
 */
export default function useProductConfigPreset() {
    const { t: $t } = useNuxtApp().$i18n;
    const { isFeatureEnabled } = useFeatureFlags();
    const { public: { productConfigPresetToolToken } } = useRuntimeConfig();
    const { resolvePlaceNames } = usePlaces();

    const schemas: Record<ProductConfigPresetType, ProductConfigPresetSchema> = {
        P2P: ProductConfigPresetSchemaP2P,
        Activity: ProductConfigPresetSchemaActivity,
    };

    /**
     * Parse, validate, and return product config presets from an encoded string.
     * Resolves place URNs to names via API.
     * Returns the parsed presets, or undefined if the input is missing or invalid.
     */
    async function parseProductConfigPreset(productKey: ProductKey, encoded: string | undefined): Promise<ProductConfigPreset | undefined> {
        const type = productConfigs.find(config => config.key === productKey)?.type as ProductConfigPresetType;
        const schema = schemas[type];
        if (!schema || !encoded) {
            return undefined;
        }

        try {
            const jsonString = isFeatureEnabled('product-config-presets.encode-url') ? decodeString(encoded, productConfigPresetToolToken) : encoded;
            const parsed = v.parse(schema, JSON.parse(jsonString));
            let preset: ProductConfigPreset = {};

            // For P2P presets, resolve place URNs to Place objects with names
            if (type === 'P2P') {
                const { origins, destinations, returnOrigins, returnDestinations, ...rest } = parsed as v.InferOutput<typeof ProductConfigPresetSchemaP2P>;
                preset = { ...rest } as ProductConfigPresetP2P;

                if (origins)
                    preset.origins = await resolvePlaceNames(origins);
                if (destinations)
                    preset.destinations = await resolvePlaceNames(destinations);
                if (returnOrigins)
                    preset.returnOrigins = await resolvePlaceNames(returnOrigins);
                if (returnDestinations)
                    preset.returnDestinations = await resolvePlaceNames(returnDestinations);
            }
            else if (type === 'Activity') {
                preset = { ...parsed } as ProductConfigPresetActivity;
            }

            // TODO - Rewire after rename
            preset.contentfulId = parsed.offerFlowContentId;

            preset.cp = encoded; // Keep original encoded string for URL re-encoding when going back from cart

            return preset;
        }
        catch (error) {
            throw createError({
                message: $t('offerFlow.productConfigPresets.validation.invalid'),
                fatal: true,
                cause: error,
            });
        }
    }

    return {
        parseProductConfigPreset,
        resolvePlaceNames,
    };
}
